import type { ComponentProps } from 'react';
import { Icon, type IconName } from './Icon.tsx';
import { cx } from './util.ts';
import './base.css';
import './Button.css';

/**
 * - primary: acción principal del agente o de la pantalla (azul de operaciones).
 * - control: decisión de una persona («Aprobar»): ámbar de control, esquinas casi rectas.
 * - secondary / ghost: acciones de apoyo. danger: «Rechazar», acciones destructivas.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'control' | 'danger';
export type ButtonSize = 'md' | 'lg';

export interface ButtonProps extends ComponentProps<'button'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: IconName;
  iconEnd?: IconName;
  /** Muestra que la acción está en curso y evita la doble pulsación. */
  loading?: boolean;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  icon,
  iconEnd,
  loading = false,
  disabled,
  type = 'button',
  className,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cx('af-button', `af-button--${variant}`, `af-button--${size}`, className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? (
        <span className="af-button__spinner" aria-hidden="true" />
      ) : (
        icon && <Icon name={icon} />
      )}
      <span className="af-button__label">{children}</span>
      {iconEnd && <Icon name={iconEnd} />}
    </button>
  );
}

export interface IconButtonProps extends Omit<ComponentProps<'button'>, 'children'> {
  /** Nombre accesible obligatorio: el botón no tiene texto visible. */
  label: string;
  icon: IconName;
  variant?: Exclude<ButtonVariant, 'control'>;
  size?: ButtonSize;
}

export function IconButton({
  label,
  icon,
  variant = 'ghost',
  size = 'md',
  type = 'button',
  className,
  ...rest
}: IconButtonProps) {
  return (
    <button
      type={type}
      aria-label={label}
      className={cx(
        'af-button',
        'af-button--icon',
        `af-button--${variant}`,
        `af-button--${size}`,
        className,
      )}
      {...rest}
    >
      <Icon name={icon} />
    </button>
  );
}
